import { CloseIcon, HeadlineSize, Modal } from '@postidigital/posti-components'
import { observer } from 'mobx-react-lite'
import React, { PropsWithChildren, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styled, { css } from 'styled-components'

import { layoutStore } from '../../store'
import { breakpoint } from '../../utils'

export interface AppModalProps {
  id: string
  isOpen: boolean
  title?: string
  titleSize?: HeadlineSize
  controls?: ReactNode
  onClose?: () => void
  showCloseButton?: boolean
  closeButtonLabel?: string
  maxWidth?: string
  contentPadding?: string
}

const AppModalComponent: React.FC<PropsWithChildren<AppModalProps>> = ({
  id,
  isOpen,
  title,
  titleSize = HeadlineSize.Seven,
  onClose,
  showCloseButton = true,
  closeButtonLabel = 'Close',
  maxWidth = '34.5rem',
  contentPadding = '2rem',
  children,
  controls,
}) => {
  if (typeof document === 'undefined') {
    return null
  }
  if (!isOpen) {
    return null
  }

  const rootElement = document.getElementById('root') || document.body
  const isFullscreen = !layoutStore.isDesktop

  return createPortal(
    <ModalLayer>
      <Modal.Backdrop
        isOpen={isOpen}
        onClick={() => {
          onClose?.()
        }}
      />
      <Modal isOpen={isOpen} isFullscreen={isFullscreen} maxWidth={maxWidth}>
        <ModalDialog
          id={id}
          $modalId={id}
          $hasTitle={!!title}
          role="dialog"
          style={{ minHeight: isFullscreen ? '100vh' : undefined }}
        >
          {showCloseButton && onClose && (
            <Modal.CloseButton
              id={`${id}_close_button`}
              type="button"
              aria-label={closeButtonLabel}
              onClick={onClose}
              contentMode="tertiary"
              hasBackground
              isOnlyIcon
              leftIcon={CloseIcon}
            />
          )}
          {title && (
            <Modal.DialogHeadlineAligner>
              <ModalTitle $modalId={id} size={titleSize}>
                {title}
              </ModalTitle>
            </Modal.DialogHeadlineAligner>
          )}
          <ModalContent $modalId={id} style={{ flex: isFullscreen ? 1 : undefined, padding: contentPadding }}>
            {children}
          </ModalContent>
          {controls && (
            <Modal.DialogButtons>
              <ModalControls>{controls}</ModalControls>
            </Modal.DialogButtons>
          )}
        </ModalDialog>
      </Modal>
    </ModalLayer>,
    rootElement
  )
}

export const AppModal = observer(AppModalComponent)

const ModalLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 110;
`

const ModalDialog = styled(Modal.Dialog).attrs<{ $modalId: string; $hasTitle: boolean }>(({ $modalId, $hasTitle }) => ({
  'aria-labelledby': $hasTitle ? `${$modalId}_title` : undefined,
  'aria-describedby': `${$modalId}_content`,
}))``

const ModalTitle = styled(Modal.DialogHeadline).attrs<{ $modalId: string }>(({ $modalId }) => ({
  id: `${$modalId}_title`,
}))``

const ModalContent = styled(Modal.DialogContent).attrs<{ $modalId: string }>(({ $modalId }) => ({
  id: `${$modalId}_content`,
}))`
  overflow-y: auto;
`

const ModalControls = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column-reverse;
    gap: ${theme.spacing.space2}rem;
    width: 100%;

    > * {
      width: 100%;
    }

    button {
      justify-content: center;
      width: 100%;
    }

    @media ${breakpoint.lg} {
      flex-direction: row;
      justify-content: flex-end;

      > *,
      button {
        width: auto;
      }
    }
  `
)
